import React, { createContext, useContext, useEffect, useState } from 'react';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useUpdateClientRowMutation } from '@/features/clients/clientsApi';
import { useUpdateDealRowMutation } from '@/features/deals/dealsApi';
import { DEAL_OVERVIEW } from '@/components/forms/CreateNewBusinessForm/validationSchema';

const TabContext = createContext(null);

export const useTabContext = () => {
	const context = useContext(TabContext);
	if (!context) {
		throw new Error('useTabContext must be used within a TabContextProvider');
	}
	return context;
};

export const TabContextProvider = ({ children }) => {
	const {
		clientRow,
		dealRow,
		clientId,
		dealId,
		isClientView: isClient,
		relationshipCompany,
		isRelationshipCompanyView,
	} = useObjectContext();

	const original = {
		title:
			(isRelationshipCompanyView
				? relationshipCompany?.name
				: isClient
					? clientRow?.name
					: dealRow?.dealName) ?? '',
		overview: (isClient ? clientRow?.clientNote : dealRow?.dealOverview) ?? '',
	};

	const [isEditing, setIsEditing] = useState(false);
	const [fields, setFields] = useState(original);

	const setFieldsState = (type, value) => {
		setFields((prev) => ({
			...prev,
			[type]: value,
		}));
	};

	// Keep the fields in sync with the row when it changes
	useEffect(() => {
		setFieldsState('title', original.title);
	}, [original.title]);

	useEffect(() => {
		setFieldsState('overview', original.overview);
	}, [original.overview]);

	const cancelEditing = () => {
		setFields(original);
		setIsEditing(false);
	};

	const titleChanged = fields.title.trim() !== original.title;
	const overviewChanged = fields.overview !== original.overview;

	const [updateClientRow, { isLoading: isUpdatingClientRow }] = useUpdateClientRowMutation();
	const [updateDealRow, { isLoading: isUpdatingDealRow }] = useUpdateDealRowMutation();

	const saveOverview = async () => {
		if (!overviewChanged || isRelationshipCompanyView) return;

		if (isClient) {
			await updateClientRow({
				clientId,
				docId: clientRow?.docId,
				properties: { clientNote: fields.overview },
			}).unwrap();
		} else {
			await updateDealRow({
				dealId,
				docId: dealRow?.id,
				properties: { [DEAL_OVERVIEW]: fields.overview },
			}).unwrap();
		}
	};

	const contextValue = {
		isEditing,
		startEditing: () => setIsEditing(true),
		stopEditing: () => setIsEditing(false),
		cancelEditing,
		fields,
		setFields: setFieldsState,
		original,
		titleChanged,
		overviewChanged,
		saveOverview,
		isSavingOverview: isUpdatingClientRow || isUpdatingDealRow,
	};

	return <TabContext.Provider value={contextValue}>{children}</TabContext.Provider>;
};
